import React, { useState, useRef, useCallback, useEffect } from 'react';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import envConfig from '../config/environment';

const VISTA1 = 'vista1';
const VISTA2 = 'vista2';
const CAPTURA = 'captura';
const OBSERVACION = 'observacion';
const CONFIRMACION = 'confirmacion';

const FLUJO_BODEGA = 'bodega';
const FLUJO_VISITA = 'visita';
const FLUJO_FUNCIONARIOS = 'funcionarios';

const OPCIONES_VISTA2 = ['EMSA', 'FRIO EXPORT', 'COIMPEXA', 'CHRONIX'];

const FLOW_LABELS = {
  [FLUJO_BODEGA]: 'Bodega',
  [FLUJO_VISITA]: 'Visita',
  [FLUJO_FUNCIONARIOS]: 'Funcionarios'
};

const FlowHeader = ({ flow, vista2Option }) => {
  if (!flow) return null;
  const title = FLOW_LABELS[flow];
  const showBodega = (flow === FLUJO_BODEGA || flow === FLUJO_FUNCIONARIOS) && vista2Option;
  const headerBg = envConfig.loginButtonColor || '#198754';
  return (
    <div
      className="rounded-t-xl overflow-hidden shadow-sm"
      style={{ backgroundColor: headerBg, color: '#ffffff' }}
    >
      <div className="px-4 py-3">
        <h2 className="text-xl font-bold leading-tight">{title}</h2>
        {showBodega && (
          <p className="text-sm mt-0.5 opacity-95">Bodega: {vista2Option}</p>
        )}
      </div>
    </div>
  );
};

const MAX_IMAGE_WIDTH = 1200;
const JPEG_QUALITY = 0.82;

const parseCedulaFromDescripcion = (desc) => {
  if (!desc || typeof desc !== 'string') return '';
  const digits = desc.replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : '';
};

const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
};

const isMobileDeviceForCamera = () => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > MAX_IMAGE_WIDTH) {
        height = (height * MAX_IMAGE_WIDTH) / width;
        width = MAX_IMAGE_WIDTH;
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if ('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(new File([blob], file.name || 'image.jpg', { type: 'image/jpeg' }));
          else reject(new Error('Compresión fallida'));
        },
        'image/jpeg',
        JPEG_QUALITY
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Error cargando imagen'));
    };
    img.src = url;
  });
};

const COMPRESS_TIMEOUT_MS = 22000;

/** Si canvas falla o tarda (HEIC, fotos muy grandes, dispositivos lentos), seguir con el archivo original. */
const compressImageOrOriginal = async (file) => {
  try {
    return await Promise.race([
      compressImage(file),
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error('compress_timeout')), COMPRESS_TIMEOUT_MS);
      })
    ]);
  } catch (e) {
    console.warn('[IngresosCharly] Compresión omitida, se usa imagen original', e?.message || e);
    return file;
  }
};

const IngresosCharly = () => {
  const { user } = useAuth();
  const [view, setView] = useState(VISTA1);
  const [flow, setFlow] = useState(null);
  const [vista2Option, setVista2Option] = useState(null);
  const [observacionVisita, setObservacionVisita] = useState('');

  const [fotoCedula, setFotoCedula] = useState(null);
  const [fotoCedulaPreview, setFotoCedulaPreview] = useState('');
  const [fotoGuia, setFotoGuia] = useState(null);
  const [fotoGuiaPreview, setFotoGuiaPreview] = useState('');
  const [nombreCedula, setNombreCedula] = useState('');
  const [cedulaNum, setCedulaNum] = useState('');
  const [loadingOcr, setLoadingOcr] = useState(false);
  const [procesandoFotoCedula, setProcesandoFotoCedula] = useState(false);
  const [procesandoFotoGuia, setProcesandoFotoGuia] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pasoCapturaBodega, setPasoCapturaBodega] = useState('cedula');
  const [pasoCapturaVisita, setPasoCapturaVisita] = useState('cedula');
  const [pasoCapturaFuncionarios, setPasoCapturaFuncionarios] = useState('elegir');
  const [cameraAbierta, setCameraAbierta] = useState(false);
  const [cameraPara, setCameraPara] = useState('cedula');
  const [catalogoFuncionarios, setCatalogoFuncionarios] = useState([]);
  const [loadingCatalogo, setLoadingCatalogo] = useState(false);
  const [selectedFuncionarioCatalog, setSelectedFuncionarioCatalog] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const inputCedulaRef = useRef(null);
  const inputGuiaRef = useRef(null);
  const canvasRef = useRef(null);

  const apiOrigin = (() => {
    try {
      if (typeof envConfig.apiUrl === 'string' && envConfig.apiUrl) {
        return new URL(envConfig.apiUrl).origin;
      }
      if (typeof window !== 'undefined') return `${window.location.protocol}//${window.location.hostname}:3004`;
    } catch (e) {}
    return '';
  })();

  const isMobileDevice = isMobile();
  const useDeviceCamera = isMobileDeviceForCamera();

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraAbierta(false);
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  useEffect(() => {
    if (!cameraAbierta || !streamRef.current || !videoRef.current) return;
    const video = videoRef.current;
    const stream = streamRef.current;
    video.srcObject = stream;
    video.play().catch(() => {});
  }, [cameraAbierta]);

  useEffect(() => {
    if (view !== CAPTURA || flow !== FLUJO_FUNCIONARIOS || pasoCapturaFuncionarios !== 'elegir' || !user?.id_generado) return;
    let cancelled = false;
    setLoadingCatalogo(true);
    const url = `${getApiUrl('SUPERVISION', 'CATALOGO')}?id_generado=${encodeURIComponent(user.id_generado)}`;
    fetch(url, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || !data.success) return;
        const list = (data.data || []).filter((item) => item.tipo === 'FUNCIONARIO');
        setCatalogoFuncionarios(list);
      })
      .catch(() => { if (!cancelled) setCatalogoFuncionarios([]); })
      .finally(() => { if (!cancelled) setLoadingCatalogo(false); });
    return () => { cancelled = true; };
  }, [view, flow, pasoCapturaFuncionarios, user?.id_generado]);

  const startCameraDesktop = useCallback(async () => {
    stopCamera();
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      setCameraAbierta(true);
    } catch (err) {
      console.error(err);
      setError('No se pudo acceder a la cámara.');
      setCameraAbierta(false);
    }
  }, [stopCamera]);

  const captureFromVideo = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.srcObject) return null;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(new File([blob], 'captura.jpg', { type: 'image/jpeg' }));
          else resolve(null);
        },
        'image/jpeg',
        JPEG_QUALITY
      );
    });
  }, []);

  const OCR_FETCH_MS = 45000;

  const runOcr = useCallback(async (file) => {
    if (!file) return { text: '', nombre: '', cedula: '' };
    setLoadingOcr(true);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), OCR_FETCH_MS);
    try {
      const formData = new FormData();
      formData.append('foto', file);
      const url = getApiUrl('BITACORA', 'EXTRACT_CEDULA');
      const res = await fetch(url, { method: 'POST', body: formData, signal: controller.signal });
      const data = await res.json();
      if (data.success && (data.nombre != null || data.cedula != null)) {
        return {
          text: '',
          nombre: (data.nombre != null ? String(data.nombre) : '').trim(),
          cedula: (data.cedula != null ? String(data.cedula) : '').trim()
        };
      }
      return { text: '', nombre: '', cedula: '' };
    } catch (e) {
      return { text: '', nombre: '', cedula: '' };
    } finally {
      clearTimeout(timer);
      setLoadingOcr(false);
    }
  }, []);

  const handleCaptureCedula = useCallback(
    async (file) => {
      if (!file || !file.size) {
        setError('No se recibió la imagen. Intente otra vez o elija desde galería.');
        return;
      }
      setError('');
      setProcesandoFotoCedula(true);
      stopCamera();
      try {
        const listo = await compressImageOrOriginal(file);
        setFotoCedula(listo);
        setFotoCedulaPreview(URL.createObjectURL(listo));
        if (flow === FLUJO_VISITA) setPasoCapturaVisita('descripcion');
        else if (flow === FLUJO_BODEGA) setPasoCapturaBodega('guia');
        setTimeout(() => {
          runOcr(listo)
            .then(({ nombre, cedula }) => {
              setNombreCedula((n) => n || nombre);
              setCedulaNum((c) => c || cedula);
            })
            .catch(() => {});
        }, 0);
      } catch (e) {
        setError('Error al procesar la imagen.');
      } finally {
        setProcesandoFotoCedula(false);
      }
    },
    [runOcr, stopCamera, flow]
  );

  const handleCaptureCedulaFuncionarios = useCallback(async (file) => {
    if (!file || !file.size) {
      setError('No se recibió la imagen. Intente otra vez o elija desde galería.');
      return;
    }
    setError('');
    setProcesandoFotoCedula(true);
    stopCamera();
    try {
      const listo = await compressImageOrOriginal(file);
      setFotoCedula(listo);
      setFotoCedulaPreview(URL.createObjectURL(listo));
      setPasoCapturaFuncionarios('revision');
      setTimeout(() => {
        runOcr(listo)
          .then(({ nombre, cedula }) => {
            setNombreCedula((n) => n || nombre);
            setCedulaNum((c) => c || cedula);
          })
          .catch(() => {});
      }, 0);
    } catch (e) {
      setError('Error al procesar la imagen.');
    } finally {
      setProcesandoFotoCedula(false);
    }
  }, [runOcr, stopCamera]);

  const handleCaptureGuia = useCallback(async (file) => {
    if (!file || !file.size) {
      setError('No se recibió la imagen. Intente otra vez.');
      return;
    }
    setError('');
    setProcesandoFotoGuia(true);
    stopCamera();
    try {
      const listo = await compressImageOrOriginal(file);
      setFotoGuia(listo);
      setFotoGuiaPreview(URL.createObjectURL(listo));
      setPasoCapturaBodega('revision');
    } catch (e) {
      setError('Error al procesar la imagen.');
    } finally {
      setProcesandoFotoGuia(false);
    }
  }, [stopCamera]);

  const handleVista1Select = (flujo) => {
    setFlow(flujo);
    setProcesandoFotoCedula(false);
    setProcesandoFotoGuia(false);
    setError('');
    setSuccess('');
    setFotoCedula(null);
    setFotoCedulaPreview('');
    setFotoGuia(null);
    setFotoGuiaPreview('');
    setNombreCedula('');
    setCedulaNum('');
    setObservacionVisita('');
    setPasoCapturaVisita('cedula');
    if (flujo === FLUJO_VISITA) setView(CAPTURA);
    else setView(VISTA2);
  };

  const handleVista2Select = (opcion) => {
    setVista2Option(opcion);
    setView(CAPTURA);
    setProcesandoFotoCedula(false);
    setProcesandoFotoGuia(false);
    setPasoCapturaBodega('cedula');
    setCameraAbierta(false);
    setFotoCedula(null);
    setFotoCedulaPreview('');
    setFotoGuia(null);
    setFotoGuiaPreview('');
    setNombreCedula('');
    setCedulaNum('');
    setError('');
    setSuccess('');
  };

  const handleVista2SelectFuncionarios = (opcion) => {
    setVista2Option(opcion);
    setView(CAPTURA);
    setProcesandoFotoCedula(false);
    setProcesandoFotoGuia(false);
    setPasoCapturaFuncionarios('elegir');
    setSelectedFuncionarioCatalog(null);
    setCameraAbierta(false);
    setFotoCedula(null);
    setFotoCedulaPreview('');
    setNombreCedula('');
    setCedulaNum('');
    setError('');
    setSuccess('');
  };

  const handleVolverVista2 = () => {
    stopCamera();
    setCameraAbierta(false);
    setProcesandoFotoCedula(false);
    setProcesandoFotoGuia(false);
    setView(VISTA2);
    setVista2Option(null);
    setPasoCapturaBodega('cedula');
    setPasoCapturaFuncionarios('elegir');
    setSelectedFuncionarioCatalog(null);
    setFotoCedula(null);
    setFotoCedulaPreview('');
    setFotoGuia(null);
    setFotoGuiaPreview('');
    setNombreCedula('');
    setCedulaNum('');
  };

  const handleVolverAPasoGuia = () => {
    stopCamera();
    setCameraAbierta(false);
    setPasoCapturaBodega('guia');
  };

  const handleVolverAPasoCedula = () => {
    stopCamera();
    setCameraAbierta(false);
    setPasoCapturaBodega('cedula');
  };

  const handleVolverVista1 = () => {
    stopCamera();
    setProcesandoFotoCedula(false);
    setProcesandoFotoGuia(false);
    setView(VISTA1);
    setFlow(null);
    setVista2Option(null);
    setPasoCapturaBodega('cedula');
    setPasoCapturaVisita('cedula');
    setPasoCapturaFuncionarios('elegir');
    setSelectedFuncionarioCatalog(null);
    setObservacionVisita('');
    setFotoCedula(null);
    setFotoCedulaPreview('');
    setFotoGuia(null);
    setFotoGuiaPreview('');
    setNombreCedula('');
    setCedulaNum('');
    setShowConfirmModal(false);
    setError('');
    setSuccess('');
  };

  const openConfirmModal = () => {
    setError('');
    if (!fotoCedula && !(flow === FLUJO_FUNCIONARIOS && selectedFuncionarioCatalog?.foto_url && parseCedulaFromDescripcion(selectedFuncionarioCatalog?.Descripcion))) {
      setError('Debe tomar la foto de la cédula.');
      return;
    }
    if (flow === FLUJO_BODEGA && !fotoGuia) {
      setError('Debe tomar la foto de la factura o guía.');
      return;
    }
    if (flow === FLUJO_FUNCIONARIOS && !fotoCedula && !selectedFuncionarioCatalog?.foto_url) {
      setError('Debe tomar la foto de la cédula.');
      return;
    }
    setShowConfirmModal(true);
  };

  const handleSubmitBodega = async () => {
    setError('');
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('idGenerado', user?.id_generado ?? '');
      formData.append('tipo', 'INGRESO_CHARLY');
      formData.append('opcionBodega', vista2Option ?? '');
      formData.append('p_nombre', nombreCedula.trim());
      formData.append('p_cedula', cedulaNum.trim());
      formData.append('username', user?.username || '');
      formData.append('appid', user?.appid || '');
      formData.append('foto', fotoCedula);
      formData.append('firma', fotoGuia);

      const res = await fetch(getApiUrl('BITACORA', 'REGISTRAR_EVENTO'), {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Error al guardar.');
        return;
      }
      setSuccess('Registro guardado correctamente.');
      setShowConfirmModal(false);
      setTimeout(handleVolverVista1, 1500);
    } catch (e) {
      setError('Error de conexión.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitFuncionarios = async () => {
    setError('');
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('idGenerado', user?.id_generado ?? '');
      formData.append('tipo', 'INGRESO_CHARLY');
      formData.append('opcionBodega', vista2Option ?? '');
      formData.append('flujoCharly', 'FUNCIONARIOS');
      formData.append('p_nombre', nombreCedula.trim());
      formData.append('p_cedula', cedulaNum.trim());
      formData.append('username', user?.username || '');
      formData.append('appid', user?.appid || '');

      const usarFotoCatalogo = selectedFuncionarioCatalog?.foto_url && !fotoCedula;
      if (usarFotoCatalogo) {
        formData.append('foto_url_catalogo', selectedFuncionarioCatalog.foto_url);
      } else if (fotoCedula) {
        formData.append('foto', fotoCedula);
      }

      const res = await fetch(getApiUrl('BITACORA', 'REGISTRAR_EVENTO'), {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Error al guardar.');
        return;
      }

      const fotoUrlGuardada = data.foto_url || null;

      if (!selectedFuncionarioCatalog && fotoUrlGuardada) {
        const catalogUrl = getApiUrl('SUPERVISION', 'CATALOGO');
        await fetch(catalogUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            id_generado: user?.id_generado,
            dato: nombreCedula.trim(),
            descripcion: cedulaNum.trim() ? `Ingresos Charly-${cedulaNum.trim()}` : 'Ingresos Charly',
            tipo: 'FUNCIONARIO',
            foto_url: fotoUrlGuardada
          })
        });
      } else if (selectedFuncionarioCatalog && fotoCedula && fotoUrlGuardada) {
        const catalogUpdateUrl = `${getApiUrl('SUPERVISION', 'CATALOGO')}/${selectedFuncionarioCatalog.id}`;
        const descBase = (selectedFuncionarioCatalog.Descripcion || '').replace(/\d{10}\s*$/, '').trim();
        await fetch(catalogUpdateUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            id_generado: user?.id_generado,
            descripcion: cedulaNum.trim() ? `${descBase}-${cedulaNum.trim()}` : descBase || 'Ingresos Charly',
            foto_url: fotoUrlGuardada
          })
        });
      }

      setSuccess('Registro guardado correctamente.');
      setShowConfirmModal(false);
      setTimeout(handleVolverVista1, 1500);
    } catch (e) {
      setError('Error de conexión.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitVisita = async () => {
    setError('');
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('idGenerado', user?.id_generado ?? '');
      formData.append('tipo', 'INGRESO_CHARLY');
      formData.append('opcionBodega', 'VISITA');
      formData.append('p_nombre', nombreCedula.trim());
      formData.append('p_cedula', cedulaNum.trim());
      formData.append('observaciones', observacionVisita.trim());
      formData.append('username', user?.username || '');
      formData.append('appid', user?.appid || '');
      formData.append('foto', fotoCedula);

      const res = await fetch(getApiUrl('BITACORA', 'REGISTRAR_EVENTO'), {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Error al guardar.');
        return;
      }
      setSuccess('Registro guardado correctamente.');
      setShowConfirmModal(false);
      setTimeout(handleVolverVista1, 1500);
    } catch (e) {
      setError('Error de conexión.');
    } finally {
      setSubmitting(false);
    }
  };

  const cardButton = (label, onClick) => (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-center px-6 py-4 rounded-xl border-2 border-indigo-200 bg-white text-gray-800 font-medium hover:bg-indigo-50 hover:border-indigo-400 transition-colors duration-200 shadow-sm"
    >
      {label}
    </button>
  );

  const getVolverClick = () => {
    if (view === VISTA2) return handleVolverVista1;
    if (view === CAPTURA && flow === FLUJO_VISITA) {
      return pasoCapturaVisita === 'revision' ? () => setPasoCapturaVisita('descripcion') : pasoCapturaVisita === 'descripcion' ? () => setPasoCapturaVisita('cedula') : handleVolverVista1;
    }
    if (view === CAPTURA && flow === FLUJO_BODEGA) {
      return pasoCapturaBodega === 'revision' ? handleVolverAPasoGuia : pasoCapturaBodega === 'guia' ? handleVolverAPasoCedula : handleVolverVista2;
    }
    if (view === CAPTURA && flow === FLUJO_FUNCIONARIOS) {
      return pasoCapturaFuncionarios === 'revision'
        ? () => setPasoCapturaFuncionarios('cedula')
        : pasoCapturaFuncionarios === 'cedula'
          ? () => { setPasoCapturaFuncionarios('elegir'); setFotoCedula(null); setFotoCedulaPreview(''); setNombreCedula(''); setCedulaNum(''); setSelectedFuncionarioCatalog(null); }
          : handleVolverVista2;
    }
    return () => {};
  };

  return (
    <Layout>
      <div className="h-full min-h-full w-full flex flex-col overflow-auto p-3 sm:p-4 md:p-6">
        <div className="w-full flex flex-col flex-1 min-h-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Ingresos Charly</h1>
          <p className="text-sm text-gray-500 mb-4">Seleccione el tipo de ingreso</p>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
          )}
          {success && (
            <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-700 text-sm">{success}</div>
          )}

          {flow != null && (view === VISTA2 || view === CAPTURA) && (
            <button type="button" onClick={getVolverClick()} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium self-start mb-2">
              ← Volver
            </button>
          )}
          {flow != null && <FlowHeader flow={flow} vista2Option={vista2Option} />}

          {view === VISTA1 && (
            <div className="grid gap-3">
              {cardButton('Bodega', () => handleVista1Select(FLUJO_BODEGA))}
              {cardButton('Visita', () => handleVista1Select(FLUJO_VISITA))}
              {cardButton('Funcionarios', () => handleVista1Select(FLUJO_FUNCIONARIOS))}
            </div>
          )}

          {view === VISTA2 && (
            <div className="bg-white rounded-t-none rounded-b-xl border border-t-0 border-gray-200 p-4 shadow-sm space-y-4">
              <p className="text-sm text-gray-600">{flow === FLUJO_BODEGA ? 'Seleccione bodega' : flow === FLUJO_FUNCIONARIOS ? 'Seleccione bodega' : 'Seleccione opción'}</p>
              <div className="grid gap-3">
                {OPCIONES_VISTA2.map((opcion) => (
                  <button
                    key={opcion}
                    type="button"
                    onClick={() => (flow === FLUJO_FUNCIONARIOS ? handleVista2SelectFuncionarios(opcion) : handleVista2Select(opcion))}
                    className="w-full flex items-center justify-center px-6 py-4 rounded-xl border-2 border-indigo-200 bg-white text-gray-800 font-medium hover:bg-indigo-50 hover:border-indigo-400 transition-colors duration-200 shadow-sm"
                  >
                    {opcion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {view === CAPTURA && flow === FLUJO_VISITA && (
            <div className="flex flex-col flex-1 min-h-0">
              <div className="bg-white rounded-t-none rounded-b-xl border border-t-0 border-gray-200 p-4 shadow-sm flex-1 flex flex-col min-h-0 space-y-4 overflow-auto">
                {pasoCapturaVisita === 'cedula' && (
                  <div className="flex flex-col flex-1 min-h-0">
                    <p className="text-2xl font-bold text-gray-900 mb-1">Capturar cédula</p>
                    <p className="text-sm text-gray-600 mb-4">Tome la foto de la cédula del visitante con el botón de abajo.</p>
                    {procesandoFotoCedula && (
                      <p className="text-sm text-indigo-600 font-medium mb-2">Procesando imagen…</p>
                    )}
                    {!cameraAbierta ? (
                      <div className="flex flex-col items-center justify-center flex-1 min-h-[200px] gap-3">
                        <button
                          type="button"
                          disabled={procesandoFotoCedula}
                          onClick={() => {
                            setCameraPara('cedula');
                            setError('');
                            if (useDeviceCamera) inputCedulaRef.current?.click();
                            else startCameraDesktop();
                          }}
                          className="px-8 py-4 rounded-xl bg-indigo-600 text-white font-medium text-lg hover:bg-indigo-700 shadow-md disabled:opacity-50 disabled:pointer-events-none"
                        >
                          Capturar
                        </button>
                        {!useDeviceCamera && (
                          <button
                            type="button"
                            disabled={procesandoFotoCedula}
                            onClick={() => { setError(''); inputCedulaRef.current?.click(); }}
                            className="px-8 py-4 rounded-xl border-2 border-indigo-600 text-indigo-600 font-medium text-lg hover:bg-indigo-50 disabled:opacity-50 disabled:pointer-events-none"
                          >
                            Subir archivo
                          </button>
                        )}
                      </div>
                    ) : (
                      <>
                        {!useDeviceCamera && (
                          <div className="relative rounded-lg overflow-hidden bg-black mb-3" style={{ minHeight: 280 }}>
                            <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-72 object-contain" />
                            <canvas ref={canvasRef} className="hidden" />
                            <button type="button" disabled={procesandoFotoCedula} onClick={async () => { const f = await captureFromVideo(); if (f) handleCaptureCedula(f); }} className="absolute bottom-3 left-1/2 -translate-x-1/2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-medium shadow disabled:opacity-50">Capturar</button>
                          </div>
                        )}
                        {!useDeviceCamera && <button type="button" onClick={stopCamera} className="text-sm text-gray-600 hover:text-gray-800">Cerrar cámara</button>}
                      </>
                    )}
                    <input ref={inputCedulaRef} type="file" accept="image/*" capture={useDeviceCamera ? 'environment' : undefined} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCaptureCedula(f); e.target.value = ''; }} />
                  </div>
                )}

                {pasoCapturaVisita === 'descripcion' && (
                  <div className="flex flex-col flex-1 min-h-0">
                    <p className="text-2xl font-bold text-gray-900 mb-1">Descripción de la visita</p>
                    <p className="text-sm text-gray-600 mb-4">Indique a dónde va el visitante o el motivo de la visita.</p>
                    <textarea
                      value={observacionVisita}
                      onChange={(e) => setObservacionVisita(e.target.value)}
                      placeholder="Ej. Oficina 101, reunión con ventas..."
                      rows={4}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <button type="button" onClick={() => setPasoCapturaVisita('revision')} className="w-full py-2.5 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700">Continuar</button>
                  </div>
                )}

                {pasoCapturaVisita === 'revision' && (
                  <>
                    <p className="text-2xl font-bold text-gray-900 mb-1">Revisar datos</p>
                    <p className="text-sm text-gray-600 mb-4">Verifique los datos extraídos de la cédula y la descripción. Luego pulse Guardar.</p>
                    {fotoCedulaPreview && <div><p className="text-xs font-medium text-gray-600 mb-1">Foto cédula</p><img src={fotoCedulaPreview} alt="Cédula" className="max-h-28 rounded-lg border object-contain" /></div>}
                    {loadingOcr && <p className="text-xs text-gray-500">Extrayendo información de la cédula...</p>}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Nombre (extraído de cédula)</label>
                        <input type="text" value={nombreCedula} onChange={(e) => setNombreCedula(e.target.value)} placeholder="Revisar o corregir" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Cédula (extraída)</label>
                        <input type="text" value={cedulaNum} onChange={(e) => setCedulaNum(e.target.value)} placeholder="Revisar o corregir" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Descripción de la visita</label>
                      <input type="text" value={observacionVisita} onChange={(e) => setObservacionVisita(e.target.value)} placeholder="Descripción" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </div>
                    <button type="button" onClick={() => openConfirmModal()} disabled={!fotoCedula || submitting} className="w-full py-2.5 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700 disabled:opacity-50">Guardar</button>
                  </>
                )}
              </div>
            </div>
          )}

          {view === CAPTURA && flow === FLUJO_BODEGA && (
            <div className="flex flex-col flex-1 min-h-0">
              <div className="bg-white rounded-t-none rounded-b-xl border border-t-0 border-gray-200 p-4 shadow-sm flex-1 flex flex-col min-h-0 space-y-4 overflow-auto">
                {/* Paso 1: Solo capturar cédula → pasa a factura (OCR en background) */}
                {pasoCapturaBodega === 'cedula' && (
                  <div className="flex flex-col flex-1 min-h-0">
                    <p className="text-2xl font-bold text-gray-900 mb-1">Capturar cédula</p>
                    <p className="text-sm text-gray-600 mb-4">Tome la foto de la cédula del visitante con el botón de abajo.</p>
                    {procesandoFotoCedula && (
                      <p className="text-sm text-indigo-600 font-medium mb-2">Procesando imagen…</p>
                    )}
                    {!cameraAbierta ? (
                      <div className="flex flex-col items-center justify-center flex-1 min-h-[200px]">
                        <button
                          type="button"
                          disabled={procesandoFotoCedula}
                          onClick={() => {
                            setCameraPara('cedula');
                            setError('');
                            if (useDeviceCamera) {
                              inputCedulaRef.current?.click();
                            } else {
                              startCameraDesktop();
                            }
                          }}
                          className="px-8 py-4 rounded-xl bg-indigo-600 text-white font-medium text-lg hover:bg-indigo-700 shadow-md disabled:opacity-50 disabled:pointer-events-none"
                        >
                          Capturar
                        </button>
                        {!useDeviceCamera && (
                          <button
                            type="button"
                            disabled={procesandoFotoCedula}
                            onClick={() => { setError(''); inputCedulaRef.current?.click(); }}
                            className="mt-3 px-8 py-4 rounded-xl border-2 border-indigo-600 text-indigo-600 font-medium text-lg hover:bg-indigo-50 disabled:opacity-50 disabled:pointer-events-none"
                          >
                            Subir archivo
                          </button>
                        )}
                      </div>
                    ) : (
                      <>
                        {!useDeviceCamera && (
                          <div className="relative rounded-lg overflow-hidden bg-black mb-3" style={{ minHeight: 280 }}>
                            <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-72 object-contain" />
                            <canvas ref={canvasRef} className="hidden" />
                            <button
                              type="button"
                              disabled={procesandoFotoCedula}
                              onClick={async () => {
                                const file = await captureFromVideo();
                                if (file) handleCaptureCedula(file);
                              }}
                              className="absolute bottom-3 left-1/2 -translate-x-1/2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-medium shadow disabled:opacity-50"
                            >
                              Capturar
                            </button>
                          </div>
                        )}
                        {!useDeviceCamera && (
                          <button type="button" onClick={stopCamera} className="text-sm text-gray-600 hover:text-gray-800">
                            Cerrar cámara
                          </button>
                        )}
                      </>
                    )}
                    <input
                      ref={inputCedulaRef}
                      type="file"
                      accept="image/*"
                      capture={useDeviceCamera ? 'environment' : undefined}
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleCaptureCedula(f);
                        e.target.value = '';
                      }}
                    />
                  </div>
                )}

                {/* Paso 2: Solo capturar factura/guía (móvil y desktop = cámara), no se analiza nada */}
                {pasoCapturaBodega === 'guia' && (
                  <div className="flex flex-col flex-1 min-h-0">
                    <p className="text-2xl font-bold text-gray-900 mb-1">Capturar factura o guía</p>
                    <p className="text-sm text-gray-600 mb-4">Tome la foto de la factura o guía. <span className="text-red-600 font-semibold">(obligatorio)</span></p>
                    {procesandoFotoGuia && (
                      <p className="text-sm text-indigo-600 font-medium mb-2">Procesando imagen…</p>
                    )}
                    {!cameraAbierta ? (
                      <div className="flex flex-col items-center justify-center flex-1 min-h-[200px]">
                        <button
                          type="button"
                          disabled={procesandoFotoGuia}
                          onClick={() => {
                            setCameraPara('guia');
                            setError('');
                            if (useDeviceCamera) {
                              inputGuiaRef.current?.click();
                            } else {
                              startCameraDesktop();
                            }
                          }}
                          className="px-8 py-4 rounded-xl bg-indigo-600 text-white font-medium text-lg hover:bg-indigo-700 shadow-md disabled:opacity-50 disabled:pointer-events-none"
                        >
                          Capturar
                        </button>
                      </div>
                    ) : (
                      <>
                        {!useDeviceCamera && (
                          <div className="relative rounded-lg overflow-hidden bg-black mb-3" style={{ minHeight: 280 }}>
                            <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-72 object-contain" />
                            <canvas ref={canvasRef} className="hidden" />
                            <button
                              type="button"
                              disabled={procesandoFotoGuia}
                              onClick={async () => {
                                const file = await captureFromVideo();
                                if (file) handleCaptureGuia(file);
                              }}
                              className="absolute bottom-3 left-1/2 -translate-x-1/2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-medium shadow disabled:opacity-50"
                            >
                              Capturar
                            </button>
                          </div>
                        )}
                        {!useDeviceCamera && (
                          <button type="button" onClick={stopCamera} className="text-sm text-gray-600 hover:text-gray-800">
                            Cerrar cámara
                          </button>
                        )}
                      </>
                    )}
                    <input
                      ref={inputGuiaRef}
                      type="file"
                      accept="image/*"
                      capture={useDeviceCamera ? 'environment' : undefined}
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleCaptureGuia(f);
                        e.target.value = '';
                      }}
                    />
                  </div>
                )}

                {/* Paso 3 (Vista 4): Revisión – fotos + inputs editables + Guardar → modal */}
                {pasoCapturaBodega === 'revision' && (
                  <>
                    <p className="text-2xl font-bold text-gray-900 mb-1">Revisar datos</p>
                    <p className="text-sm text-gray-600 mb-4">Verifique las fotos y corrija nombre o cédula si hace falta. Luego pulse Guardar.</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Foto cédula</p>
                        {fotoCedulaPreview && <img src={fotoCedulaPreview} alt="Cédula" className="w-full rounded-lg border object-cover aspect-[3/4] max-h-36" />}
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Foto factura/guía</p>
                        {fotoGuiaPreview && <img src={fotoGuiaPreview} alt="Factura/Guía" className="w-full rounded-lg border object-cover aspect-[3/4] max-h-36" />}
                      </div>
                    </div>
                    {loadingOcr && <p className="text-xs text-gray-500">Extrayendo información de la cédula...</p>}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Nombre (extraído de cédula)</label>
                        <input
                          type="text"
                          value={nombreCedula}
                          onChange={(e) => setNombreCedula(e.target.value)}
                          placeholder="Revisar o corregir"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Cédula (extraída)</label>
                        <input
                          type="text"
                          value={cedulaNum}
                          onChange={(e) => setCedulaNum(e.target.value)}
                          placeholder="Revisar o corregir"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={openConfirmModal}
                      disabled={!fotoCedula || !fotoGuia || submitting}
                      className="w-full py-2.5 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700 disabled:opacity-50"
                    >
                      Guardar
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {view === CAPTURA && flow === FLUJO_FUNCIONARIOS && (
            <div className="flex flex-col flex-1 min-h-0">
              <div className="bg-white rounded-t-none rounded-b-xl border border-t-0 border-gray-200 p-4 shadow-sm flex-1 flex flex-col min-h-0 space-y-4 overflow-auto">
                {pasoCapturaFuncionarios === 'elegir' && (
                  <div className="flex flex-col flex-1 min-h-0 space-y-4">
                    <p className="text-2xl font-bold text-gray-900 mb-1">Nombre</p>
                    <p className="text-sm text-gray-600 mb-2">Elija un funcionario o agregue uno nuevo.</p>
                    {loadingCatalogo && <p className="text-sm text-gray-500">Cargando catálogo...</p>}
                    {!loadingCatalogo && (
                      <div>
                        <label htmlFor="funcionario-select" className="block text-sm font-medium text-gray-700 mb-1">Nombre:</label>
                        <select
                          id="funcionario-select"
                          value={selectedFuncionarioCatalog ? String(selectedFuncionarioCatalog.id) : 'seleccione'}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === 'seleccione' || val === '') {
                              setSelectedFuncionarioCatalog(null);
                              setNombreCedula('');
                              setCedulaNum('');
                            } else if (val === 'agregar') {
                              setSelectedFuncionarioCatalog(null);
                              setNombreCedula('');
                              setCedulaNum('');
                              setFotoCedula(null);
                              setFotoCedulaPreview('');
                              setPasoCapturaFuncionarios('cedula');
                            } else {
                              const item = catalogoFuncionarios.find((f) => String(f.id) === val);
                              if (item) {
                                const cedulaDesc = parseCedulaFromDescripcion(item.Descripcion);
                                const completo = cedulaDesc.length === 10 && !!item.foto_url;
                                setNombreCedula(item.Dato || '');
                                setCedulaNum(cedulaDesc || '');
                                setSelectedFuncionarioCatalog(item);
                                if (completo) {
                                  setShowConfirmModal(true);
                                } else {
                                  setPasoCapturaFuncionarios('cedula');
                                }
                              }
                            }
                          }}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="seleccione">Seleccione</option>
                          <option value="agregar">Agregar</option>
                          {catalogoFuncionarios.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.Dato || '—'}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {pasoCapturaFuncionarios === 'cedula' && (
                  <div className="flex flex-col flex-1 min-h-0">
                    <p className="text-2xl font-bold text-gray-900 mb-1">Capturar cédula</p>
                    <p className="text-sm text-gray-600 mb-4">Tome la foto de la cédula del funcionario con el botón de abajo.</p>
                    {procesandoFotoCedula && (
                      <p className="text-sm text-indigo-600 font-medium mb-2">Procesando imagen…</p>
                    )}
                    {!cameraAbierta ? (
                      <div className="flex flex-col items-center justify-center flex-1 min-h-[200px] gap-3">
                        <div className="flex flex-wrap justify-center gap-3">
                          <button
                            type="button"
                            disabled={procesandoFotoCedula}
                            onClick={() => {
                              setCameraPara('cedula');
                              setError('');
                              if (useDeviceCamera) inputCedulaRef.current?.click();
                              else startCameraDesktop();
                            }}
                            className="px-8 py-4 rounded-xl bg-indigo-600 text-white font-medium text-lg hover:bg-indigo-700 shadow-md disabled:opacity-50 disabled:pointer-events-none"
                          >
                            Capturar
                          </button>
                          {!useDeviceCamera && (
                            <button
                              type="button"
                              disabled={procesandoFotoCedula}
                              onClick={() => { setError(''); inputCedulaRef.current?.click(); }}
                              className="px-8 py-4 rounded-xl border-2 border-indigo-600 text-indigo-600 font-medium text-lg hover:bg-indigo-50 disabled:opacity-50 disabled:pointer-events-none"
                            >
                              Subir archivo
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        {!useDeviceCamera && (
                          <div className="relative rounded-lg overflow-hidden bg-black mb-3" style={{ minHeight: 280 }}>
                            <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-72 object-contain" />
                            <canvas ref={canvasRef} className="hidden" />
                            <button type="button" disabled={procesandoFotoCedula} onClick={async () => { const f = await captureFromVideo(); if (f) handleCaptureCedulaFuncionarios(f); }} className="absolute bottom-3 left-1/2 -translate-x-1/2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-medium shadow disabled:opacity-50">Capturar</button>
                          </div>
                        )}
                        {!useDeviceCamera && <button type="button" onClick={stopCamera} className="text-sm text-gray-600 hover:text-gray-800">Cerrar cámara</button>}
                      </>
                    )}
                    <input ref={inputCedulaRef} type="file" accept="image/*" capture={useDeviceCamera ? 'environment' : undefined} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCaptureCedulaFuncionarios(f); e.target.value = ''; }} />
                  </div>
                )}

                {pasoCapturaFuncionarios === 'revision' && (
                  <>
                    <p className="text-2xl font-bold text-gray-900 mb-1">Revisar datos</p>
                    <p className="text-sm text-gray-600 mb-4">Verifique los datos extraídos de la cédula. Luego pulse Guardar.</p>
                    {fotoCedulaPreview && <div><p className="text-xs font-medium text-gray-600 mb-1">Foto cédula</p><img src={fotoCedulaPreview} alt="Cédula" className="max-h-28 rounded-lg border object-contain" /></div>}
                    {loadingOcr && <p className="text-sm text-indigo-600 font-medium">Extrayendo información de la cédula… Puede editar los campos mientras tanto.</p>}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Nombre (extraído de cédula)</label>
                        <input type="text" value={nombreCedula} onChange={(e) => setNombreCedula(e.target.value)} placeholder="Revisar o corregir" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Cédula (extraída)</label>
                        <input type="text" value={cedulaNum} onChange={(e) => setCedulaNum(e.target.value)} placeholder="Revisar o corregir" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                    </div>
                    <button type="button" onClick={() => openConfirmModal()} disabled={!fotoCedula || submitting || loadingOcr} className="w-full py-2.5 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700 disabled:opacity-50">Guardar</button>
                  </>
                )}
              </div>
            </div>
          )}

          {view === CONFIRMACION && flow !== FLUJO_BODEGA && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-4">
              <p className="text-sm font-semibold text-gray-800">Resumen</p>
              <div className="text-sm text-gray-600 space-y-2">
                {flow === FLUJO_FUNCIONARIOS && <p>Opción: <strong>{vista2Option}</strong></p>}
                {flow === FLUJO_VISITA && <p>Lugar: <strong>{observacionVisita || '—'}</strong></p>}
              </div>
              <button type="button" onClick={handleVolverVista1} className="w-full py-2.5 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700">
                Cerrar
              </button>
            </div>
          )}
        </div>
      </div>

      {showConfirmModal && flow === FLUJO_BODEGA && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden">
            <div
              className="px-4 py-3 text-white rounded-t-xl"
              style={{ backgroundColor: envConfig.loginButtonColor || '#198754' }}
            >
              <h2 className="text-xl font-bold leading-tight">Bodega</h2>
              <p className="text-sm mt-0.5 opacity-95">Bodega: {vista2Option}</p>
            </div>
            <div className="p-6 space-y-4 border border-t-0 border-gray-200 rounded-b-xl">
              <p className="text-sm text-gray-600">Revise que los datos sean correctos antes de enviar.</p>
              <div className="space-y-3 text-sm bg-gray-50 rounded-lg p-4">
                <p><span className="font-medium text-gray-700">Bodega:</span> <strong>{vista2Option}</strong></p>
                <p><span className="font-medium text-gray-700">Nombre:</span> <strong>{nombreCedula || '—'}</strong></p>
                <p><span className="font-medium text-gray-700">Cédula:</span> <strong>{cedulaNum || '—'}</strong></p>
                <p><span className="font-medium text-gray-700">Foto cédula:</span> Adjunta</p>
                <p><span className="font-medium text-gray-700">Foto factura/guía:</span> Adjunta</p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium text-sm hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubmitBodega}
                  disabled={submitting}
                  className="flex-1 py-2 rounded-lg text-white font-medium text-sm disabled:opacity-50"
                  style={{ backgroundColor: envConfig.loginButtonColor }}
                >
                  {submitting ? 'Guardando...' : 'Enviar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showConfirmModal && flow === FLUJO_VISITA && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden">
            <div
              className="px-4 py-3 text-white rounded-t-xl"
              style={{ backgroundColor: envConfig.loginButtonColor || '#198754' }}
            >
              <h2 className="text-xl font-bold leading-tight">Visita</h2>
            </div>
            <div className="p-6 space-y-4 border border-t-0 border-gray-200 rounded-b-xl">
              <p className="text-sm text-gray-600">Revise los datos antes de enviar.</p>
              <div className="space-y-3 text-sm bg-gray-50 rounded-lg p-4">
                <p><span className="font-medium text-gray-700">Nombre:</span> <strong>{nombreCedula || '—'}</strong></p>
                <p><span className="font-medium text-gray-700">Cédula:</span> <strong>{cedulaNum || '—'}</strong></p>
                <p><span className="font-medium text-gray-700">Descripción:</span> <strong>{observacionVisita || '—'}</strong></p>
                <p><span className="font-medium text-gray-700">Foto cédula:</span> Adjunta</p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium text-sm hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubmitVisita}
                  disabled={submitting}
                  className="flex-1 py-2 rounded-lg text-white font-medium text-sm disabled:opacity-50"
                  style={{ backgroundColor: envConfig.loginButtonColor }}
                >
                  {submitting ? 'Guardando...' : 'Aceptar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showConfirmModal && flow === FLUJO_FUNCIONARIOS && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden">
            <div
              className="px-4 py-3 text-white rounded-t-xl"
              style={{ backgroundColor: envConfig.loginButtonColor || '#198754' }}
            >
              <h2 className="text-xl font-bold leading-tight">Funcionarios</h2>
              {vista2Option && <p className="text-sm mt-0.5 opacity-95">Bodega: {vista2Option}</p>}
            </div>
            <div className="p-6 space-y-4 border border-t-0 border-gray-200 rounded-b-xl">
              <p className="text-sm text-gray-600">Revise los datos antes de enviar.</p>
              {(selectedFuncionarioCatalog?.foto_url && !fotoCedula) || fotoCedulaPreview ? (
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-1">Foto cédula</p>
                  <img
                    src={
                      fotoCedulaPreview
                        ? fotoCedulaPreview
                        : selectedFuncionarioCatalog?.foto_url?.startsWith('http')
                          ? selectedFuncionarioCatalog.foto_url
                          : apiOrigin && selectedFuncionarioCatalog?.foto_url
                            ? `${apiOrigin}${selectedFuncionarioCatalog.foto_url.startsWith('/') ? '' : '/'}${selectedFuncionarioCatalog.foto_url}`
                            : ''
                    }
                    alt="Cédula"
                    className="w-full max-h-28 object-contain rounded-lg border border-gray-200 bg-gray-50"
                  />
                </div>
              ) : null}
              <div className="space-y-3 text-sm bg-gray-50 rounded-lg p-4">
                <p><span className="font-medium text-gray-700">Bodega:</span> <strong>{vista2Option || '—'}</strong></p>
                <p><span className="font-medium text-gray-700">Nombre:</span> <strong>{nombreCedula || '—'}</strong></p>
                <p><span className="font-medium text-gray-700">Cédula:</span> <strong>{cedulaNum || '—'}</strong></p>
                <p><span className="font-medium text-gray-700">Foto cédula:</span> <strong>Adjunta</strong></p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium text-sm hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubmitFuncionarios}
                  disabled={submitting}
                  className="flex-1 py-2 rounded-lg text-white font-medium text-sm disabled:opacity-50"
                  style={{ backgroundColor: envConfig.loginButtonColor }}
                >
                  {submitting ? 'Guardando...' : 'Aceptar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default IngresosCharly;
